import { useLanguageContext } from "@/hooks/context-hooks";
import classNames from "classnames";
import { useEffect, useMemo } from "react";
import CheckboxInput from "./fields/CheckboxInput";
import GroupInput from "./fields/GroupInput";
import LabelInput from "./fields/LabelInput";
import MultiRangeInput from "./fields/MultiRangeInput";
import MultiSelectInput from "./fields/MultiSelectInput";
import RadioInput from "./fields/RadioInput";
import RangeInput from "./fields/RangeInput";
import SelectInput from "./fields/SelectInput";
import TextareaInput from "./fields/TextareaInput";
import ToggleSwitchInput from "./fields/ToggleSwitchInput";
import YearInput from "./fields/YearInput";
import TextInput from "./fields/TextInput";
import NumberInput from "./fields/NumberInput";
import DateInput from "./fields/DateInput";
import DateTimeInput from "./fields/DateTimeInput";
import TimeInput from "./fields/TimeInput";
import MonthInput from "./fields/MonthInput";
import PillsInput from "./fields/PillsInput";

const hasOtherOption = (options) => {
    if (!Array.isArray(options)) return false;
    return options.some(opt => {
        if (!opt) return false;
        if (typeof opt === "string") {
            return opt.toUpperCase() === "OTHER";
        }
        const valStr = opt.value !== undefined && opt.value !== null ? String(opt.value).toUpperCase() : "";
        const labelStr = opt.label !== undefined && opt.label !== null ? String(opt.label).toUpperCase() : "";
        return valStr === "-1" || valStr === "OTHER" || labelStr === "OTHER";
    });
};

export default function FormField({
    field,
    optionsMap = {},
    fetchOptions = () => { },
    nestedField = false,
    handleInputChange,
    formState,
    errors = null,
    containerClassName = null,
    defaultContainerClassName = "col-xl-6 col-lg-12 col-md-6",
    disableContainer = false,
}) {
    const { language } = useLanguageContext();

    const options = useMemo(() => {
        if (optionsMap[field.name]) {
            return optionsMap[field.name];
        }
        if (field.options && Array.isArray(field.options)) {
            return field.options;
        }
        return [];
    }, [optionsMap, field.name]); // Fetch options from the optionsMap

    useEffect(() => {
        if (field.name !== '' && field.options && typeof field.options === "function" && !options.length) {
            fetchOptions(field.name, field.options, field.optionsKey);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.name, options.length]);

    const resolveFieldLabel = (fieldLabel) => {
        if (typeof fieldLabel === "function") {
            return fieldLabel(formState);
        }
        return fieldLabel;
    };

    const translateValue = (value) => {
        return language(value) || value;
    };

    const replaceFieldNameWithLabel = (message, fieldName, fieldLabel) => {
        return message.replace(fieldName, translateValue(resolveFieldLabel(fieldLabel)) || "")
    }

    const shouldShowField = (field) => {
        if (typeof field.showCondition === "function") {
            return field.showCondition(formState);
        }
        // Hide field until its dependsOn parent has a selected value
        if (field.dependsOn) {
            const parentValue = formState[field.dependsOn];
            return parentValue !== undefined && parentValue !== null && parentValue !== "";
        }
        return true;
    };

    let fieldHtml = null;
    switch (field.type) {
        case "text": {
            fieldHtml = <TextInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} onChange={handleInputChange} />;
            break;
        }
        case "number": {
            fieldHtml = <NumberInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} onChange={handleInputChange} />;
            break;
        }
        case "date": {
            fieldHtml = <DateInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} future={field.future} onChange={handleInputChange} />;
            break;
        }
        case "month": {
            fieldHtml = <MonthInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} disabled={field.disabled} onChange={handleInputChange} />;
            break;
        }
        case "datetime": {
            fieldHtml = <DateTimeInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} disabled={field.disabled} onChange={handleInputChange} />;
            break;
        }
        case "time": {
            fieldHtml = <TimeInput name={field.name} value={formState[field.name] || ""} placeholder={translateValue(field.placeholder)} disabled={field.disabled} onChange={handleInputChange} />;
            break;
        }
        case "year": {
            fieldHtml = <YearInput name={field.name} value={formState[field.name]} future={field.future} onChange={handleInputChange} />
            break;
        }
        case "select": {
            const translatedOptions = options.map((opt) => {
                if (typeof opt === "string") {
                    const translatedValue = translateValue(opt);
                    return { label: translatedValue, value: translatedValue };
                }
                return {
                    ...opt,
                    label: translateValue(opt.label),
                    value: translateValue(opt.value),
                };
            });

            fieldHtml = (
                <>
                    <SelectInput name={field.name} value={formState[field.name] || ""} options={translatedOptions} disabled={field.disabled} onChange={handleInputChange} />
                    {hasOtherOption(options) && (formState[field.name] === "-1" || formState[field.name] === -1 || String(formState[field.name]).toUpperCase() === "OTHER") && (
                        <div className="mt-2">
                            <TextInput
                                name={`${field.name}_manualValue`}
                                value={formState[`${field.name}_manualValue`] || ""}
                                placeholder={translateValue("Enter brand name manually")}
                                onChange={handleInputChange}
                            />
                            {(errors !== null && errors[`${field.name}_manualValue`]) && (
                                <div className="fs-8 mt-1">
                                    <small className="text-danger">{errors[`${field.name}_manualValue`]}</small>
                                </div>
                            )}
                        </div>
                    )}
                </>
            );
            break;
        }
        case "multiselect": {
            const translatedOptions = options.map((opt) => {
                if (typeof opt === "string") {
                    const translatedValue = translateValue(opt);
                    return { label: translatedValue, value: translatedValue };
                }
                return {
                    ...opt,
                    label: translateValue(opt.label),
                    value: translateValue(opt.value),
                };
            });
            fieldHtml = <MultiSelectInput name={field.name} label={translateValue(resolveFieldLabel(field.label))} options={translatedOptions} onChange={handleInputChange} value={formState[field.name] || []} />
            break;
        }
        case "pills": {
            const translatedOptions = options.map((opt) => {
                if (typeof opt === "string") {
                    const translatedValue = translateValue(opt);
                    return { label: translatedValue, value: translatedValue };
                }
                return {
                    ...opt,
                    label: translateValue(opt.label),
                    value: opt.value,
                };
            });
            fieldHtml = (
                <PillsInput
                    name={field.name}
                    options={translatedOptions}
                    multiple={field.multiple !== false}
                    onChange={handleInputChange}
                    value={formState[field.name]}
                    disabled={field.disabled}
                />
            );
            break;
        }
        case "radio": {
            const translatedOptions = (field.options || []).map((opt) => {
                if (typeof opt === "string") {
                    return { label: translateValue(opt), value: opt };
                }
                return {
                    ...opt,
                    label: translateValue(opt.label),
                };
            });

            fieldHtml = (
                <RadioInput
                    name={field.name}
                    value={formState[field.name]}
                    options={translatedOptions}
                    stacked={field.stacked}
                    disabled={field.disabled}
                    onChange={handleInputChange}
                    form={field.form}
                />
            );
            break;
        }
        case "checkbox": {
            const translatedOptions = (field.options || []).map((opt) => {
                if (typeof opt === "string") {
                    return { label: translateValue(opt), value: opt };
                }
                return {
                    ...opt,
                    label: translateValue(opt.label),
                };
            });

            if (field.singleChecked) {
                fieldHtml = (
                    <RadioInput
                        name={field.name}
                        value={formState[field.name]}
                        options={translatedOptions}
                        stacked={field.stacked}
                        disabled={field.disabled}
                        inputType="checkbox"
                        onChange={handleInputChange}
                    />
                );
                break;
            }

            fieldHtml = (
                <CheckboxInput
                    name={field.name}
                    options={translatedOptions}
                    stacked={field.stacked}
                    disabled={field.disabled}
                    formState={formState}
                    onChange={handleInputChange}
                />
            );
            break;
        }
        case "textarea": {
            fieldHtml = (
                <TextareaInput
                    name={field.name}
                    value={formState[field.name] || ""}
                    placeholder={translateValue(field.placeholder)}
                    rows={field.rows}
                    cols={field.cols}
                    onChange={handleInputChange}
                />
            );
            break;
        }
        case "group": {
            fieldHtml = (
                <GroupInput
                    fields={field.group?.fields || []}
                    renderField={(groupField, index, noOfFields) => {
                        let cols = 12 / noOfFields;
                        if (noOfFields > 6) {
                            cols = 12 / 6;
                        }

                        return (
                            <div key={index} className={classNames(`col-${cols}`, groupField.className, !groupField.className && { 'pe-1': index === 0, 'ps-1': index === noOfFields - 1, 'px-1': index > 0 && index < noOfFields - 1 })}>
                                <FormField key={groupField.label} field={groupField} optionsMap={optionsMap} fetchOptions={fetchOptions} nestedField={true} handleInputChange={handleInputChange} formState={formState} errors={errors} />
                            </div>
                        );
                    }}
                />
            );
            break;
        }
        case "range": {
            fieldHtml = <RangeInput name={field.name} value={formState[field.name] || "0"} min={field.min} max={field.max} step={field.step} onChange={handleInputChange} />
            break;
        }
        case "multirange": {
            fieldHtml = <MultiRangeInput name={field.name} min={field.min} max={field.max} step={field.step || 1} value={formState[field.name] || { min: field.min, max: field.max }} onChange={handleInputChange} />
            break;
        }
        case "toggleswitch": {
            fieldHtml = <ToggleSwitchInput name={field.name} value={formState[field.name] || field?.options?.[0]} options={field.options} onChange={handleInputChange} />
            break;
        }
        default: fieldHtml = null;
    }

    if (!shouldShowField(field)) {
        return null;
    }

    if (nestedField || disableContainer) {
        return (<>
            <LabelInput label={translateValue(resolveFieldLabel(field.label))} className={field.className} required={field.required} />
            {fieldHtml}
            {(errors !== null && errors[field.name]) && <div className="fs-8"><small className="text-danger">{replaceFieldNameWithLabel(errors[field.name], field.name, field.label)}</small></div>}
        </>);
    }

    let resolvedContainerClassName = field?.className || containerClassName || defaultContainerClassName;
    if (!field?.className && field?.cols) {
        const colsValue = Number(field.cols);
        if (colsValue > 0 && colsValue <= 12) {
            resolvedContainerClassName = `col-xl-${colsValue} col-lg-12 col-md-${colsValue}`;
        }
    }

    return (
        <div className={resolvedContainerClassName} key={field?.label}>
            <LabelInput label={translateValue(resolveFieldLabel(field.label))} className={field.className} required={field.required} />
            {fieldHtml}
            {(errors !== null && errors[field.name]) && <div className="fs-8"><small className="text-danger">{replaceFieldNameWithLabel(errors[field.name], field.name, field.label)}</small></div>}
        </div>
    );
}
